import { sanitizeRichHtml } from '@/lib/sanitizeRichHtml';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2 } from 'lucide-react';
import type { MindtapEnrollmentPreview } from '@/types/course-key';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading: boolean;
  preview: MindtapEnrollmentPreview | null;
  title?: string;
};

export function MindtapEnrollmentPreviewDialog({
  open,
  onOpenChange,
  loading,
  preview,
  title = 'Release overview',
}: Props) {
  const instructionsHtml = preview?.instructionsHtml?.trim()
    ? preview.instructionsHtml
    : preview?.rulesHtml || '';

  const keyRows =
    preview?.keys && preview.keys.length > 0
      ? preview.keys
      : preview?.courseKey || preview?.courseKeyMasked
        ? [
            {
              name: 'Course Key',
              courseKey: preview.courseKey,
              courseKeyMasked: preview.courseKeyMasked,
            },
          ]
        : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {preview
              ? `${preview.studentName} — ${preview.programName}`
              : 'Preview what the student will receive'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : preview ? (
          <div className="space-y-4 text-sm">
            <div className="rounded-md border p-3 space-y-1">
              <p>
                <span className="text-muted-foreground">Start date: </span>
                {preview.startDate}
              </p>
              <p>
                <span className="text-muted-foreground">Portal: </span>
                <a
                  href={preview.portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline break-all"
                >
                  {preview.portalUrl || '—'}
                </a>
              </p>
            </div>

            <div>
              <p className="mb-2 font-medium">Course keys</p>
              {keyRows.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>MindTap name</TableHead>
                        <TableHead>Course key</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {keyRows.map((row) => (
                        <TableRow key={row.name}>
                          <TableCell className="font-medium">{row.name}</TableCell>
                          <TableCell className="font-mono text-xs">
                            {row.courseKey || row.courseKeyMasked || 'Not released yet'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted-foreground">Not released yet</p>
              )}
            </div>

            {instructionsHtml.trim() && (
              <div>
                <p className="mb-1 font-medium">Instructions</p>
                <div
                  className="prose prose-sm max-w-none rounded-md border p-3 [&_img]:my-3 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-lg"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeRichHtml(instructionsHtml),
                  }}
                />
              </div>
            )}

            {preview.instructionFiles.length > 0 && (
              <div>
                <p className="mb-1 font-medium">Instruction files</p>
                <ul className="list-disc pl-5 space-y-1">
                  {preview.instructionFiles.map((file) => (
                    <li key={file.fileUrl}>
                      <a
                        href={file.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {file.fileName}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No preview available.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
